import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ClientType, Organization } from 'src/app/models/organization';
import { User } from 'src/app/models/user';
import { AdminService } from 'src/app/services/admin.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { UtilsService } from 'src/app/services/utils.service';
import { Option } from '../../components/select/select.component';

@Component({
  selector: 'app-create-organization-dialog',
  templateUrl: './create-organization-dialog.component.html',
  styleUrls: ['./create-organization-dialog.component.scss']
})
export class CreateOrganizationDialogComponent {
  organization?: Organization;

  user?: User;

  organizationToCreate: {
    name?: string,
    headquartersAddress?: string,
    logo?: any,
    clientType?: ClientType,
  } = {
      name: undefined,
      headquartersAddress: undefined,
      logo: undefined,
      clientType: ClientType.CLIENT
    };

  clientTypeOptions: Option[] = Object.values(ClientType)
    .map((clientType) => ({
      label: `CREATE_ORGANIZATION_DIALOG.CLIENT_TYPE_${clientType.toUpperCase()}`,
      value: clientType,
    }));

  logo?: File;

  loading = false;

  userToInvite: {
    firstName?: string,
    lastName?: string,
    email?: string,
    role?: string
  } = {
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      role: 'admin'
    };

  roles: {
    label: string,
    value: string,
    selected: boolean,
    disabled: boolean,
    level: number
  }[] = [
      {
        label: 'USER_ROLES.CASE_OPERATOR',
        value: 'case_operator',
        selected: false,
        disabled: false,
        level: 10
      },
      {
        label: 'USER_ROLES.COUNTRY_OPERATOR',
        value: 'country_operator',
        selected: false,
        disabled: false,
        level: 10
      },
      {
        label: 'USER_ROLES.MANAGER',
        value: 'manager',
        selected: false,
        disabled: false,
        level: 50
      },
      {
        label: 'USER_ROLES.ADMIN',
        value: 'admin',
        selected: false,
        disabled: false,
        level: 100
      }
    ]

  errors: {
    [key: string]: {
      value: boolean,
      message?: string
    }
  } = {
      firstName: {
        value: false,
        message: undefined
      },
      lastName: {
        value: false,
        message: undefined
      },
      email: {
        value: false,
        message: undefined
      },
      roles: {
        value: false,
        message: undefined
      },
      name: {
        value: false,
        message: undefined
      },
      headquartersAddress: {
        value: false,
        message: undefined
      },
      clientType: {
        value: false,
        message: undefined
      }
    }


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private adminService: AdminService,
    private authService: AuthenticationService,
    private dialogRef: MatDialogRef<CreateOrganizationDialogComponent>,
    private organizationService: OrganizationService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private utils: UtilsService
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
      this.roles = this.roles.filter(role => {
        return role.level <= (this.user?.role?.level ?? 0);
      })
    });
  }

  ngOnInit(): void {
    this.organization = this.data.organization;
  }

  ngOnDestroy(): void {
  }

  isDisabled() {
    return this.loading;
  }

  /** The first administrator is optional: invited only when one of its fields is filled. */
  hasAdministrator(): boolean {
    return [this.userToInvite.firstName, this.userToInvite.lastName, this.userToInvite.email].some((value) => !this.isBlank(value));
  }

  _hasErrors() {
    const withAdministrator = this.hasAdministrator();
    this.errors['firstName'] = {
      value: false,
      message: undefined
    }
    if (withAdministrator && this.isBlank(this.userToInvite.firstName)) {
      this.errors['firstName'].value = true;
      this.errors['firstName'].message = this.translate.instant('ERRORS.USER_FIRSTNAME_REQUIRED');
    }

    this.errors['lastName'] = {
      value: false,
      message: undefined
    }
    if (withAdministrator && this.isBlank(this.userToInvite.lastName)) {
      this.errors['lastName'].value = true;
      this.errors['lastName'].message = this.translate.instant('ERRORS.USER_LASTNAME_REQUIRED');
    }

    this.errors['email'] = {
      value: false,
      message: undefined
    }
    if (withAdministrator && this.isBlank(this.userToInvite.email)) {
      this.errors['email'].value = true;
      this.errors['email'].message = this.translate.instant('ERRORS.USER_EMAIL_REQUIRED');
    } else if (withAdministrator && !this.utils.validateEmailPattern(this.userToInvite.email!.trim())) {
      this.errors['email'].value = true;
      this.errors['email'].message = this.translate.instant('ERRORS.EMAIL_FORMAT');
    }

    this.errors['role'] = {
      value: false,
      message: undefined
    }
    if (withAdministrator && !this.userToInvite.role) {
      this.errors['role'].value = true;
      this.errors['role'].message = this.translate.instant('ERRORS.USER_ROLE_REQUIRED');
    }

    this.errors['name'] = {
      value: false,
      message: undefined
    }
    if (this.isBlank(this.organizationToCreate.name)) {
      this.errors['name'].value = true;
      this.errors['name'].message = this.translate.instant('ERRORS.ORGANIZATION_NAME_REQUIRED');
    }

    this.errors['headquartersAddress'] = {
      value: false,
      message: undefined
    }
    if (this.isBlank(this.organizationToCreate.headquartersAddress)) {
      this.errors['headquartersAddress'].value = true;
      this.errors['headquartersAddress'].message = this.translate.instant('ERRORS.ORGANIZATION_HEADQUARTERS_ADDRESS_REQUIRED');
    }

    this.errors['clientType'] = {
      value: false,
      message: undefined
    }
    if (!this.organizationToCreate.clientType) {
      this.errors['clientType'].value = true;
      this.errors['clientType'].message = this.translate.instant('ERRORS.ORGANIZATION_CLIENT_TYPE_REQUIRED');
    }

    return this.errors['firstName'].value || this.errors['lastName'].value ||
      this.errors['email'].value || this.errors['name'].value ||
      this.errors['headquartersAddress'].value ||
      this.errors['clientType'].value;
  }

  private isBlank(value?: string): boolean {
    return !value?.trim();
  }

  onClientTypeChange(event: any) {
    if (event && event.value && event.value.value) {
      this.organizationToCreate.clientType = event.value.value;
    }
  }

  onCancel(): void {
    this.dialogRef.close({
      created: false
    });
  }

  async onLogoChange(logo?: any) {
    this.logo = logo;
    if (this.logo === undefined && this.organizationToCreate) {
      this.organizationToCreate.logo = undefined;
    } else if (this.logo) {
      const res = await this.organizationService.uploadLogo(this.logo);
      this.organizationToCreate.logo = res.url;
    }
  }

  async onCreateAndInvite() {
    if (this._hasErrors()) {
      return;
    }
    const organization = {
      ...this.organizationToCreate,
      name: this.organizationToCreate.name?.trim(),
      headquartersAddress: this.organizationToCreate.headquartersAddress?.trim()
    };
    // No role sent: the back-end makes the first user an Admin.
    const user = this.hasAdministrator() ? {
      firstName: this.userToInvite.firstName?.trim(),
      lastName: this.userToInvite.lastName?.trim(),
      email: this.userToInvite.email?.trim()
    } : undefined;
    this.loading = true;
    try {
      await this.adminService.createOrganization(organization, user);
      this.dialogRef.close({ created: true, organization, user });
    } catch (err: any) {
      // The dialog stays open: what was typed is kept, and the refusal is shown next to its field when it has one.
      const message = err?.error?.message;
      if (message === 'ORGANIZATION_NAME_ALREADY_EXISTS') {
        this.errors['name'] = { value: true, message: this.translate.instant('ERRORS.ORGANIZATION_NAME_ALREADY_EXISTS') };
      } else if (message === 'DUPLICATE_ENTRY') {
        this.errors['email'] = { value: true, message: this.translate.instant('ERRORS.DUPLICATE_ENTRY') };
      } else if (message && this.utils.hasTranslation(`ERRORS.${message}`)) {
        this.toastr.error(`ERRORS.${message}`);
      } else {
        this.toastr.error('ERRORS.GENERIC');
      }
    } finally {
      this.loading = false;
    }
  }


}
